import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Paper, Button, Chip, Divider } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SeoHead from "../components/SeoHead";
import blogPosts from "../data/blogPosts";

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <Box sx={{ textAlign: "center", mt: 8 }}>
        <Typography variant="h5" gutterBottom>
          Post Not Found
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          The blog post you are looking for does not exist.
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/blog")}>
          Back to Blog
        </Button>
      </Box>
    );
  }

  const currentIndex = blogPosts.findIndex((p) => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost =
    currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  return (
    <Box id="blog-post-page" sx={{ maxWidth: 800, mx: "auto", mt: 4 }}>
      <SeoHead
        title={post.title}
        description={post.description}
        canonical={`/blog/${post.slug}`}
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/blog")}
        sx={{ mb: 2, color: "text.secondary" }}
      >
        Back to Blog
      </Button>

      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          backgroundColor: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              mb: 2,
            }}
          >
            <Chip
              label={post.category}
              size="small"
              sx={{
                backgroundColor: "rgba(52,152,219,0.12)",
                color: "#3498db",
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {post.readTime}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(post.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </Typography>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
            {post.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {post.description}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3, borderColor: "rgba(255,255,255,0.08)" }} />

        {post.content.map((block, i) => {
          if (block.startsWith("## ")) {
            return (
              <Typography
                key={i}
                variant="h5"
                sx={{ mt: i === 0 ? 0 : 4, mb: 2, fontWeight: "bold" }}
              >
                {block.replace("## ", "")}
              </Typography>
            );
          }
          return (
            <Typography
              key={i}
              variant="body1"
              color="text.secondary"
              paragraph
              sx={{ lineHeight: 1.8 }}
            >
              {block}
            </Typography>
          );
        })}
      </Paper>

      {/* Navigation between posts */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 4,
          mb: 4,
          gap: 2,
        }}
      >
        {prevPost ? (
          <Button
            onClick={() => navigate(`/blog/${prevPost.slug}`)}
            sx={{ color: "text.secondary", textAlign: "left", flex: 1 }}
          >
            <Box>
              <Typography variant="caption" display="block">
                Previous
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 300,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {prevPost.title}
              </Typography>
            </Box>
          </Button>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
        {nextPost ? (
          <Button
            onClick={() => navigate(`/blog/${nextPost.slug}`)}
            sx={{ color: "text.secondary", textAlign: "right", flex: 1 }}
          >
            <Box>
              <Typography variant="caption" display="block">
                Next
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 300,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {nextPost.title}
              </Typography>
            </Box>
          </Button>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
      </Box>
    </Box>
  );
}
